<template>
    <div class="min-h-screen" style="background-color: #F1F5F7;">
        <!-- Header -->
        <div class="bg-white shadow-sm border-b border-gray-200">
            <div class="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
                <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <!-- Back Link & Title -->
                    <div>
                        <a :href="`/campaigns/${campaign.id}`"
                           class="text-pink-600 hover:text-pink-700 font-medium inline-flex items-center text-sm">
                            <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path>
                            </svg>
                            Back to Campaign
                        </a>
                        <h1 class="text-xl sm:text-2xl font-bold text-gray-900 mt-2" style="font-family: 'Red Hat Text', sans-serif;">
                            Our Impact
                        </h1>
                        <p class="text-sm text-gray-600 mt-1">{{ campaign.name }}</p>
                    </div>

                    <!-- Share Button -->
                    <div>
                        <button @click="shareModalContext = 'impact'; showShareModal = true"
                                class="inline-flex items-center px-4 py-2 bg-gradient-to-r from-pink-500 to-pink-600 text-white font-bold rounded-lg hover:from-pink-600 hover:to-pink-700 transition-all transform hover:scale-105 text-sm sm:text-base">
                            <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"></path>
                            </svg>
                            Share My Impact
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Tab Navigation -->
        <div class="bg-white border-b border-gray-200">
            <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <nav class="flex space-x-2 sm:space-x-8" style="font-family: 'Red Hat Text', sans-serif;">
                    <button
                        @click="activeTab = 'your-campaign'"
                        :class="[
                            'py-4 px-1 border-b-2 font-medium text-sm sm:text-base transition-colors',
                            activeTab === 'your-campaign'
                                ? 'border-pink-500 text-pink-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        ]"
                    >
                        <span class="flex items-center">
                            <svg class="w-5 h-5 mr-1 sm:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
                            </svg>
                            <span class="hidden sm:inline">Your Campaign</span>
                            <span class="sm:hidden">Your</span>
                        </span>
                    </button>
                    <button
                        @click="activeTab = 'leaderboard'"
                        :class="[
                            'py-4 px-1 border-b-2 font-medium text-sm sm:text-base transition-colors',
                            activeTab === 'leaderboard'
                                ? 'border-pink-500 text-pink-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        ]"
                    >
                        <span class="flex items-center">
                            <svg class="w-5 h-5 mr-1 sm:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"></path>
                            </svg>
                            <span class="hidden sm:inline">Foundation Leaderboard</span>
                            <span class="sm:hidden">Leaderboard</span>
                        </span>
                    </button>
                </nav>
            </div>
        </div>

        <!-- Tab Content -->
        <div class="max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
            <!-- Your Campaign Tab -->
            <div v-show="activeTab === 'your-campaign'" class="tab-content">
                <your-campaign-tab
                    :campaign="campaign"
                    :breakdown="breakdown"
                    :cohort-stats="cohortStats"
                    :rank-data="rankData"
                    :badges="badges"
                    @open-share-modal="shareModalContext = 'campaign'; showShareModal = true"
                />
            </div>

            <!-- Leaderboard Tab -->
            <div v-show="activeTab === 'leaderboard'" class="tab-content">
                <leaderboard-tab
                    :leaderboard-data="leaderboardData"
                    :foundation="foundation"
                    :current-campaign-id="campaign.id"
                />
            </div>
        </div>

        <!-- Share Modal with ShareLink component -->
        <div v-if="showShareModal" class="fixed inset-0 z-50 overflow-y-auto" style="background-color: rgba(0, 0, 0, 0.5);">
            <div class="flex items-center justify-center min-h-screen px-4">
                <div @click.self="showShareModal = false" class="fixed inset-0"></div>
                <div class="relative bg-white rounded-lg max-w-md w-full p-6 shadow-xl">
                    <div class="flex justify-between items-center mb-4">
                        <h3 class="text-lg font-bold text-gray-900">{{ shareModalTitle }}</h3>
                        <button @click="showShareModal = false" class="text-gray-400 hover:text-gray-600">
                            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                            </svg>
                        </button>
                    </div>
                    <p class="text-gray-600 mb-4">{{ shareModalDescription }}</p>

                    <!-- Use ShareLink component -->
                    <share-link
                        :campaign-id="campaign.id"
                        :campaign="campaign"
                        :show-header="false"
                        layout="inline"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import YourCampaignTab from './YourCampaignTab.vue'
import LeaderboardTab from './LeaderboardTab.vue'
import ShareLink from '../../share-link/ShareLink.vue'

export default {
    name: 'CampaignImpactDashboard',

    components: {
        YourCampaignTab,
        LeaderboardTab,
        ShareLink,
    },

    props: {
        campaign: {
            type: Object,
            required: true
        },
        breakdown: {
            type: Object,
            required: true
        },
        cohortStats: {
            type: Object,
            required: true
        },
        rankData: {
            type: Object,
            required: true
        },
        badges: {
            type: Object,
            required: true
        },
        leaderboardData: {
            type: Array,
            required: true
        },
        foundation: {
            type: Object,
            required: true
        }
    },

    data() {
        return {
            activeTab: 'your-campaign', // 'your-campaign' or 'leaderboard'
            showShareModal: false,
            shareModalContext: 'impact', // 'impact' or 'campaign'
        }
    },

    computed: {
        shareModalTitle() {
            return this.shareModalContext === 'impact' ? 'Share My Impact' : 'Share Your Campaign'
        },
        shareModalDescription() {
            return this.shareModalContext === 'impact'
                ? "Share your campaign's impact with your network!"
                : "Share your campaign with your network and grow your donor base!"
        }
    },

    mounted() {
        // Check URL hash for deep linking
        const hash = window.location.hash.replace('#', '')
        if (hash === 'leaderboard') {
            this.activeTab = 'leaderboard'
        }
    },

    watch: {
        activeTab(newTab) {
            // Update URL hash for deep linking
            window.location.hash = newTab
        }
    }
}
</script>

<style scoped>
.tab-content {
    animation: fadeIn 0.3s ease-in-out;
}

@keyframes fadeIn {
    from {
        opacity: 0;
        transform: translateY(10px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}
</style>
